// Properties API - matching admin-web structure
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { 
    IBasicPropertyCreate,
    IProperty, 
    IPropertyApiResponse, 
    IPropertyDetailsResponse, 
    IPropertyEntrataConfigResponse, 
    IPropertyFeesResponse, 
    IPropertyListResponse, 
    IPropertyMaintenanceHoursResponse, 
    IPropertyMerchantAccountsResponse 
} from '@/types/admin/properties';

// RTK Query API slice for properties (matching admin-web pattern)
export const propertiesApi = createApi({
  reducerPath: 'propertiesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Properties',
    'PropertyDetails',
    'PropertyFees',
    'PropertyEntrataConfig',
    'PropertyMerchantAccounts',
    'PropertyMaintenanceHours',
    'PropertyCbxWifi',
  ],
  endpoints: (builder) => ({
    // List all properties
    listProperties: builder.query<IPropertyListResponse, string | void>({
      query: (queryString = '') => ({
        url: `/api/v1/properties/${queryString}`,
        method: 'GET',
      }),
      providesTags: ['Properties'],
    }),

    // Get single property
    getProperty: builder.query<IPropertyDetailsResponse, number>({
      query: (propertyId) => ({
        url: `/api/v1/properties/${propertyId}`,
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    // Create new property (backend only accepts basic fields)
    postProperty: builder.mutation<IPropertyApiResponse, { property: IBasicPropertyCreate }>({
      query: ({ property }) => ({
        url: '/api/v1/properties',
        method: 'POST',
        body: property,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Properties'],
    }),

    // Update property
    putProperty: builder.mutation<
      IPropertyApiResponse,
      { propertyId: number; property: Partial<IProperty> }
    >({
      query: ({ propertyId, property }) => ({
        url: `/api/v1/properties/${propertyId}`,
        method: 'PUT',
        body: property,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        'Properties',
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    // Get property fees
    getPropertyFees: builder.query<IPropertyFeesResponse, number>({
      query: (propertyId) => ({
        url: `/api/v1/properties/${propertyId}/fees`,
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [
        { type: 'PropertyFees', id: propertyId },
      ],
    }),

    // Update property fees
    putPropertyFees: builder.mutation<
      IPropertyApiResponse,
      { propertyId: number; fees: Record<string, unknown> }
    >({
      query: ({ propertyId, fees }) => ({
        url: `/api/v1/properties/${propertyId}/fees`,
        method: 'PUT',
        body: fees,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'PropertyFees', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    // Get property Entrata configuration
    getPropertyEntrataConfiguration: builder.query<IPropertyEntrataConfigResponse, number>({
      query: (propertyId) => ({
        url: `/api/v1/properties/${propertyId}/entrata-configuration`,
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [
        { type: 'PropertyEntrataConfig', id: propertyId },
      ],
    }),

    // Update property Entrata configuration
    putPropertyEntrataConfiguration: builder.mutation<
      IPropertyApiResponse,
      { propertyId: number; entrataConfiguration: Record<string, unknown> }
    >({
      query: ({ propertyId, entrataConfiguration }) => ({
        url: `/api/v1/properties/${propertyId}/entrata-configuration`,
        method: 'PUT',
        body: entrataConfiguration,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'PropertyEntrataConfig', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    // Get property merchant accounts
    getPropertyMerchantAccounts: builder.query<IPropertyMerchantAccountsResponse, number>({
      query: (propertyId) => ({
        url: `/api/v1/properties/${propertyId}/merchant-accounts`,
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [
        { type: 'PropertyMerchantAccounts', id: propertyId },
      ],
    }),

    // Update property merchant accounts
    putPropertyMerchantAccounts: builder.mutation<
      IPropertyApiResponse,
      { propertyId: number; merchantAccounts: Record<string, unknown> }
    >({
      query: ({ propertyId, merchantAccounts }) => ({
        url: `/api/v1/properties/${propertyId}/merchant-accounts`,
        method: 'PUT',
        body: merchantAccounts,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'PropertyMerchantAccounts', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    // Get property maintenance hours
    getPropertyMaintenanceHours: builder.query<IPropertyMaintenanceHoursResponse, number>({
      query: (propertyId) => ({
        url: `/api/v1/properties/${propertyId}/maintenance-hours`,
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [
        { type: 'PropertyMaintenanceHours', id: propertyId },
      ],
    }),

    // Update property maintenance hours
    putPropertyMaintenanceHours: builder.mutation<
      IPropertyApiResponse,
      { propertyId: number; maintenanceHours: Record<string, unknown> }
    >({
      query: ({ propertyId, maintenanceHours }) => ({
        url: `/api/v1/properties/${propertyId}/maintenance-hours`,
        method: 'PUT',
        body: maintenanceHours,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'PropertyMaintenanceHours', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    // Get property CBX WiFi
    getPropertyCbxWifi: builder.query<IPropertyApiResponse, number>({
      query: (propertyId) => ({
        url: `/api/v1/properties/${propertyId}/cbx-wifi`,
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [
        { type: 'PropertyCbxWifi', id: propertyId },
      ],
    }),

    // Upload property CBX WiFi file
    putPropertyCbxWifi: builder.mutation<
      IPropertyApiResponse,
      { propertyId: number; cbxWifi: File }
    >({
      query: ({ propertyId, cbxWifi }) => {
        const formData = new FormData();
        formData.append('cbxWifi', cbxWifi);
        
        return {
          url: `/api/v1/properties/${propertyId}/cbx-wifi`,
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'PropertyCbxWifi', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useListPropertiesQuery,
  useLazyListPropertiesQuery,
  useGetPropertyQuery,
  useLazyGetPropertyQuery,
  usePostPropertyMutation,
  usePutPropertyMutation,
  useGetPropertyFeesQuery,
  useLazyGetPropertyFeesQuery,
  usePutPropertyFeesMutation,
  useGetPropertyEntrataConfigurationQuery,
  useLazyGetPropertyEntrataConfigurationQuery,
  usePutPropertyEntrataConfigurationMutation,
  useGetPropertyMerchantAccountsQuery,
  useLazyGetPropertyMerchantAccountsQuery,
  usePutPropertyMerchantAccountsMutation,
  useGetPropertyMaintenanceHoursQuery,
  useLazyGetPropertyMaintenanceHoursQuery,
  usePutPropertyMaintenanceHoursMutation,
  useGetPropertyCbxWifiQuery,
  useLazyGetPropertyCbxWifiQuery,
  usePutPropertyCbxWifiMutation,
} = propertiesApi;

